import type { AxiosRequestConfig } from 'axios';
import urlcat from 'urlcat';
import type {
    ListSearchOptions,
    ListSearchResponse,
    ModuleConstructorProps,
    NotificationReportCreateRequest,
    NotificationReportQueryCreateRequest,
    NotificationReportQueryDetectColumnsRequest,
} from '../Types';
import type { NotificationReport } from '../Types/EntityTypes';
import type {
    NotificationReportQuery,
    NotificationReportQueryDeltaBaselineResponse,
    NotificationReportQueryDetectColumnsResponse,
    NotificationReportQueryDeltaCacheResponse,
    NotificationReportRunsResponse,
} from '../Types/Responses';
import { generateArrayQSParams } from '../Lib';
import { ListEndpointFunc } from './BaseEntityModuleFuncs';

const Notifications = ({ endpoints, httpClient }: ModuleConstructorProps) => ({
    listReports: (options: ListSearchOptions = {}, config?: AxiosRequestConfig) =>
        ListEndpointFunc<ListSearchResponse<NotificationReport>>(httpClient)(
            {
                endpoint: endpoints.notifications.listReports,
                options,
            },
            config,
        ),
    getReport: (id: number, includes: string[] = [], config?: AxiosRequestConfig) =>
        httpClient.get<{ data: NotificationReport }>(
            `${urlcat(endpoints.notifications.getReport, { id })}${generateArrayQSParams(
                includes ?? [],
                'include',
                '?',
            )}`,
            config,
        ),
    createReport: (payload: NotificationReportCreateRequest, config?: AxiosRequestConfig) =>
        httpClient.post<{ data: NotificationReport }>(
            endpoints.notifications.createReport,
            payload,
            config,
        ),
    previewReport: (id: number, config?: AxiosRequestConfig) =>
        httpClient.post(`${urlcat(endpoints.notifications.previewReport, { id })}`, {}, config),
    getReportRuns: (id: number, config?: AxiosRequestConfig) =>
        httpClient.get<NotificationReportRunsResponse>(
            urlcat(endpoints.notifications.getReportRuns, { id }),
            config,
        ),
    createReportQuery: (
        payload: NotificationReportQueryCreateRequest,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ data: NotificationReportQuery }>(
            endpoints.notifications.createReportQuery,
            payload,
            config,
        ),
    detectReportQueryColumns: (
        payload: NotificationReportQueryDetectColumnsRequest,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<NotificationReportQueryDetectColumnsResponse>(
            endpoints.notifications.detectReportQueryColumns,
            payload,
            config,
        ),
    getReportQueryDeltaCache: (id: number, config?: AxiosRequestConfig) =>
        httpClient.get<NotificationReportQueryDeltaCacheResponse>(
            urlcat(endpoints.notifications.getReportQueryDeltaCache, { id }),
            config,
        ),
    clearReportQueryDeltaCache: (id: number, config?: AxiosRequestConfig) =>
        httpClient.delete<NotificationReportQueryDeltaCacheResponse>(
            urlcat(endpoints.notifications.clearReportQueryDeltaCache, { id }),
            config,
        ),
    seedReportQueryDeltaBaseline: (id: number, config?: AxiosRequestConfig) =>
        httpClient.post<NotificationReportQueryDeltaBaselineResponse>(
            urlcat(endpoints.notifications.seedReportQueryDeltaBaseline, { id }),
            {},
            config,
        ),
    updateReportQuery: (
        id: number,
        payload: Pick<
            NotificationReportQuery,
            | 'query'
            | 'filename'
            | 'source'
            | 'send_new_rows'
            | 'send_updated_rows'
            | 'delta_key_columns'
        >,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.put<{ data: NotificationReportQuery }>(
            urlcat(endpoints.notifications.updateReportQuery, { id }),
            payload,
            config,
        ),
    deleteReportQuery: (id: number, config?: AxiosRequestConfig) =>
        httpClient.delete<{ data: NotificationReportQuery }>(
            urlcat(endpoints.notifications.deleteReportQuery, { id }),
            config,
        ),
});

export default Notifications;
